import {
  fork,
  put,
  all,
  call,
  take,
  cancel,
  select,
  takeLatest,
  takeLeading,
} from 'redux-saga/effects';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import { toDateFirebase } from '../../helpers/sharedFunction';
import toastr from 'toastr';
import types from './actionTypes';
import * as actions from './actions';

const db = getFirestore(rsf.app);
const functions = getFunctions(rsf.app);

const normalizeDesign = (design) => {
  if (!design) return design;
  if (typeof design !== 'string') return design;

  try {
    return JSON.parse(design);
  } catch (error) {
    return design;
  }
};

const landingPageTransformer = (payload) => {
  const landingPages = [];
  payload.forEach((doc) => {
    const data = doc.data();

    landingPages.push({
      id: doc.id,
      ...data,
      design: normalizeDesign(data.design),
      createdAt: toDateFirebase(doc, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(doc, data, 'updatedAt').toDate(),
      }),
    });
  });
  return landingPages;
};

function* syncLandingPagesSaga() {
  const countryId = yield select((state) => state.Dashboard.countryId);

  const landingPagesRef = query(
    collection(db, 'landingPages'),
    where('countryId', '==', countryId),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, landingPagesRef, {
    successActionCreator: actions.syncLandingPagesSuccess,
    failureActionCreator: actions.syncLandingPagesFailure,
    transform: (payload) => landingPageTransformer(payload),
  });

  yield take(types.RESET_LANDING_PAGE_STATE);
  yield cancel(task);
}

function* saveLandingPageSaga({ page }) {
  try {
    const countryId = yield select((state) => state.Dashboard.countryId);
    // countryId travels on every save, create and update alike: the callable
    // requires it, and on update it compares it against the stored value to
    // authorize the caller. Omitting it on update made republishing fail.
    const payload = { ...page, countryId };
    const fn = httpsCallable(functions, 'landingPageSave');
    const { data } = yield call(fn, payload);
    if (data.error) throw new Error(data.error.message);
    if (!data.data || !data.data.landingId || !data.data.publicUrl) {
      throw new Error('Landing page save returned an invalid response.');
    }

    yield put(
      actions.saveLandingPageSuccess(data.data.landingId, data.data.publicUrl),
    );
    toastr.success('Landing page published!', '');
  } catch (error) {
    yield put(actions.saveLandingPageFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* archiveLandingPageSaga({ landingId }) {
  try {
    const fn = httpsCallable(functions, 'landingPageSave');
    const { data } = yield call(fn, { archived: true, landingId });
    if (data.error) throw new Error(data.error.message);

    yield put(actions.archiveLandingPageSuccess());
    toastr.success('Landing page archived!', '');
  } catch (error) {
    yield put(actions.archiveLandingPageFailure(error));
    toastr.error(error.message, 'Error');
  }
}

function* landingPageSaga() {
  yield all([
    takeLatest(types.SYNC_LANDING_PAGES.REQUEST, syncLandingPagesSaga),
    takeLeading(types.SAVE_LANDING_PAGE.REQUEST, saveLandingPageSaga),
    takeLeading(types.ARCHIVE_LANDING_PAGE.REQUEST, archiveLandingPageSaga),
  ]);
}

export default landingPageSaga;
